// FlaggedReportsPage.jsx
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';

// Helper: show status with a colored badge
const StatusBadge = ({ status }) => (
  <span className={`badge badge-${status.toLowerCase()}`}>
    {status}
  </span>
);

function FlaggedReportsPage() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('New'); // "New", "Resolved", "Dismissed"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [undoStack, setUndoStack] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalReport, setModalReport] = useState(null);

  // Fetch reports (all statuses)
const fetchReports = async () => {
  setLoading(true);
  setError('');
  try {
    const res = await api.get('/admin/reports?all=1');
    console.debug('[FlaggedReportsPage] Reports loaded:', res.data);
    setReports(Array.isArray(res.data) ? res.data : res.data.reports || []);
  } catch (err) {
    console.error('[FlaggedReportsPage] Failed to load reports:', err);
    setError('Failed to load flagged reports.');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => { fetchReports(); }, []);

  // Status filter
  const filteredReports = Array.isArray(reports)
  ? reports.filter(r => filter === 'All' || r.status === filter)
  : [];

  // Status update with admin comment
  const handleStatusUpdate = async (reportId, newStatus) => {
    let comment = '';
    if (window.confirm(`Are you sure you want to mark this report as "${newStatus}"?`)) {
      comment = window.prompt('Optional: Add an admin comment (visible to other admins/audit trail):', '');
      try {
        await api.put(`/admin/reports/${reportId}`, { status: newStatus, admin_comment: comment });
        setUndoStack(prev => [
          ...prev,
          { report: reports.find(r => r.report_id === reportId), prevStatus: 'New' }
        ]);
        setReports(prev => prev.filter(r => r.report_id !== reportId));
      } catch (err) {
        console.error(`[FlaggedReportsPage] Status update failed:`, err);
        alert('Failed to update report status.');
      }
    }
  };

  // Bulk select
  const toggleSelect = id => setSelected(sel =>
    sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]
  );

  // Bulk actions
  const handleBulkStatusUpdate = async newStatus => {
    if (selected.length === 0) return;
    if (!window.confirm(`Change status of ${selected.length} reports to "${newStatus}"?`)) return;
    for (let id of selected) {
      await handleStatusUpdate(id, newStatus); // serial for simplicity/debug
    }
    setSelected([]);
  };

  // Undo (client-side, for this session only)
  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const { report, prevStatus } = undoStack[undoStack.length - 1];
    try {
      await api.put(`/admin/reports/${report.report_id}`, { status: prevStatus });
      setReports(prev => [report, ...prev]);
      setUndoStack(stack => stack.slice(0, -1));
    } catch (err) {
      alert('Undo failed.');
    }
  };

  // Modal preview for full question
  const handlePreview = report => {
    setModalReport(report);
    setShowModal(true);
  };

  // UI
  return (
    <div className="admin-container">
      <h2>Flagged Questions</h2>
      <p>Review questions reported by users for errors, inaccuracies, or inappropriate content. Click "Edit" to modify the question directly.</p>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: 10 }}>
        <button onClick={fetchReports}>🔄 Refresh</button>
        <div>
          {['New', 'Resolved', 'Dismissed', 'All'].map(tab => (
            <button
              key={tab}
              className={`filter-tab${filter === tab ? ' active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        {selected.length > 0 &&
          <span>
            <button className="action-button resolve"
              onClick={() => handleBulkStatusUpdate('Resolved')}
            >Bulk Resolve</button>
            <button className="action-button delete"
              onClick={() => handleBulkStatusUpdate('Dismissed')}
            >Bulk Dismiss</button>
            <span style={{ marginLeft: 10 }}>{selected.length} selected</span>
          </span>
        }
        {undoStack.length > 0 &&
          <button style={{ marginLeft: 'auto' }} onClick={handleUndo}>Undo Last Action</button>
        }
      </div>

      {loading && <div className="loading-card">Loading flagged reports...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredReports.length === 0 &&
        <div className="empty-card">
          🎉 All clear! No flagged questions to review right now.
        </div>
      }

      {!loading && !error && filteredReports.length > 0 &&
        <table className="reports-table">
          <thead>
            <tr>
              <th><input type="checkbox"
                checked={selected.length === filteredReports.length}
                onChange={e => setSelected(e.target.checked ? filteredReports.map(r => r.report_id) : [])}
              /></th>
              <th>Question</th>
              <th>Status</th>
              <th>Reason</th>
              <th>Reporter</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.map(report => (
              <tr key={report.report_id} className={`status-${report.status.toLowerCase()}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.includes(report.report_id)}
                    onChange={() => toggleSelect(report.report_id)}
                  />
                </td>
                <td>
                  <button
                    className="link-button"
                    onClick={() => handlePreview(report)}
                    title="Preview full question"
                  >
                    {report.question_text.slice(0, 60)}...
                  </button>
                </td>
                <td><StatusBadge status={report.status} /></td>
                <td>{report.report_reason}</td>
                <td>{report.reporter_email}</td>
                <td>{new Date(report.created_at).toLocaleString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Link to={`/admin/questions/${report.question_id}/edit`}>
                      <button className="action-button edit">Edit</button>
                    </Link>
                    {report.status === 'New' && (
                      <>
                        <button
                          className="action-button resolve"
                          onClick={() => handleStatusUpdate(report.report_id, 'Resolved')}
                        >✔ Resolve</button>
                        <button
                          className="action-button delete"
                          onClick={() => handleStatusUpdate(report.report_id, 'Dismissed')}
                        >✖ Dismiss</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }

      {/* Modal for full question preview */}
      {showModal && modalReport &&
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Question Preview</h3>
            <p><strong>Question:</strong> {modalReport.question_text}</p>
            <ul>
              {['a', 'b', 'c', 'd', 'e'].map(letter =>
                modalReport[`choice_${letter}`] &&
                  <li key={letter}>
                    <strong>{letter.toUpperCase()}:</strong> {modalReport[`choice_${letter}`]}
                  </li>
              )}
            </ul>
            <p><strong>Correct Choice:</strong> {modalReport.correct_choice}</p>
            <p><strong>Rationale:</strong> {modalReport.rationale}</p>
            <button onClick={() => setShowModal(false)}>Close</button>
          </div>
        </div>
      }
    </div>
  );
}

export default FlaggedReportsPage;

/*
CSS notes for AdminPage.css:
- .badge.badge-new { background: orange; }
- .badge.badge-resolved { background: green; }
- .badge.badge-dismissed { background: #888; }
- .modal-overlay { ... } .modal { ... }
- .loading-card, .empty-card { ... }
- .link-button { background: none; border: none; color: blue; cursor: pointer; text-decoration: underline; }
- Responsive table or stacked cards for small screens.
*/
